/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect } from 'react';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import IconButton from '@mui/material/IconButton';
import FilterListIcon from '@mui/icons-material/FilterAlt';
import Tooltip from '@mui/material/Tooltip';
import { styled } from '@mui/material/styles';
import SearchIcon from '@mui/icons-material/Search';
import Popover from '@mui/material/Popover';
import MenuItem from '@mui/material/MenuItem';
import Checkbox from '@mui/material/Checkbox';
import './styles.css';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Variable from '../../constants/constants.json';

const { createRequest } = require('../../api/client');

const CustomTooltip = styled(({ className, ...props }) => (
  <Tooltip {...props} classes={{ popper: className }} arrow />
))(({ theme }) => ({
  [`& .MuiTooltip-tooltip`]: {
    backgroundColor: '#FFFFFF',
    color: '#000000',
    boxShadow: theme.shadows[1],
    fontSize: '14px',
    padding: '10px 20px',
  },
}));

const Card = ({ text, count, tooltipItems }) => {
  const cardContent = (
    <div className="card">
      <div className="card-content">
        <div className="card-name">{text}</div>
        {count > 1 && <div className="card-count">{count}</div>}
      </div>
    </div>
  );

  return text === "Salesforce" && tooltipItems && tooltipItems.length > 0 ? (
    <CustomTooltip
      title={
        <ul style={{ margin: 0, paddingLeft: '16px' }}>
          {tooltipItems.map((item, index) => (
            <li key={index}>{item}</li>
          ))}
        </ul>
      }
    >
      {cardContent}
    </CustomTooltip>
  ) : (
    cardContent
  );
};

export default function BasicTable() {
  const [rows, setRows] = useState([]);

  const [uniqueContentSources, setUniqueContentSources] = useState([]);
  const [uniqueSearchClients, setUniqueSearchClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [anchorElSource, setAnchorElSource] = useState(null);
  const [anchorElClient, setAnchorElClient] = useState(null);
  const [selectedSources, setSelectedSources] = useState(['All']);
  const [selectedClients, setSelectedClients] = useState(['All']);

  const [page, setPage] = useState(0);
  const [rowsPerPage] = useState(10);

  const [sourceSearch, setSourceSearch] = useState('');
  const [clientSearch, setClientSearch] = useState('');

  const [anchorElTenant, setAnchorElTenant] = useState(null);
  const [selectedTenants, setSelectedTenants] = useState(['All']);
  const [tenantSearch, setTenantSearch] = useState('');
  const [uniqueTenantNames, setUniqueTenantNames] = useState([]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const options = {
          url: `${Variable.backendUrl}/techStack/tech-stack`,
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          },
        };

        const data = await createRequest(options);

        setRows(data);

        const contentSourcesSet = new Set();
        const searchClientsSet = new Set();

        data.forEach(item => {
          const contentSources = item.contentSources || [];
          const searchClients = item.searchClients || [];

          contentSources.forEach(source => {
            contentSourcesSet.add(Object.keys(source)[0]);
          });

          searchClients.forEach(client => {
            searchClientsSet.add(Object.keys(client)[0]);
          });
        });

        const tenantNamesArray = data.map(item => item.tenantName)

        const contentSourcesArray = Array.from(contentSourcesSet);
        const searchClientsArray = Array.from(searchClientsSet);

        setUniqueTenantNames(tenantNamesArray);
        setUniqueContentSources(contentSourcesArray);
        setUniqueSearchClients(searchClientsArray);

        setSelectedTenants(["All", ...tenantNamesArray]);
        setSelectedSources(['All', ...contentSourcesArray]);
        setSelectedClients(['All', ...searchClientsArray]);

      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  useEffect(() => {
    setSelectedTenants(['All', ...uniqueTenantNames]);
    setSelectedSources(['All', ...uniqueContentSources]);
    setSelectedClients(['All', ...uniqueSearchClients]);
  }, [uniqueTenantNames, uniqueContentSources, uniqueSearchClients]);

  const handleTenantClick = (event) => {
    setAnchorElTenant(event.currentTarget);
  };
  
  const handleCloseTenant = () => {
    setAnchorElTenant(null);
  };

  const handleSourceClick = (event) => {
    setAnchorElSource(event.currentTarget);
  };

  const handleClientClick = (event) => {
    setAnchorElClient(event.currentTarget);
  };

  const handleCloseSource = () => {
    setAnchorElSource(null);
  };

  const handleCloseClient = () => {
    setAnchorElClient(null);
  };

  const handleToggleTenants = (tenant) => {
    
    if (tenant === 'All') {
      
      if (selectedTenants.length === uniqueTenantNames.length + 1) {
        setSelectedTenants([]);
      } else {
        setSelectedTenants(['All', ...uniqueTenantNames]);
      }
    } else {
      let newSelectedTenants;
      if (selectedTenants.includes(tenant)) {
        newSelectedTenants = selectedTenants.filter((t) => t !== tenant && t !== 'All');
      } else {
        newSelectedTenants = [...selectedTenants, tenant];
      }
      if (newSelectedTenants.length === uniqueTenantNames.length) {
        newSelectedTenants = ['All', ...uniqueTenantNames];
      }
      setSelectedTenants(newSelectedTenants);
    }
  };
  
  const handleToggleSources = (source) => {
    if (source === 'All') {
      if (selectedSources.length === uniqueContentSources.length + 1) {
        setSelectedSources([]);
      } else {
        setSelectedSources(['All', ...uniqueContentSources]);
      }
    } else {
      let newSelectedSources;
      if (selectedSources.includes(source)) {
        newSelectedSources = selectedSources.filter((s) => s !== source && s !== 'All');
      } else {
        newSelectedSources = [...selectedSources, source];
      }
      if (newSelectedSources.length === uniqueContentSources.length) {
        newSelectedSources = ['All', ...uniqueContentSources];
      }
      setSelectedSources(newSelectedSources);
    }
  };

  const handleToggleClients = (client) => {
    if (client === 'All') {
      if (selectedClients.length === uniqueSearchClients.length + 1) {
        setSelectedClients([]);
      } else {
        setSelectedClients(['All', ...uniqueSearchClients]);
      }
    } else {
      let newSelectedClients;
      if (selectedClients.includes(client)) {
        newSelectedClients = selectedClients.filter((c) => c !== client && c !== 'All');
      } else {
        newSelectedClients = [...selectedClients, client];
      }
      if (newSelectedClients.length === uniqueSearchClients.length) {
        newSelectedClients = ['All', ...uniqueSearchClients];
      }
      setSelectedClients(newSelectedClients);
    }
  };

  const calculateSourceCounts = (contentSources) => {
    const sourceCounts = {};
  
    contentSources.forEach(source => {
      const sourceName = Object.keys(source)[0];
      const count = Object.values(source)[0];
  
      if (!sourceCounts[sourceName]) {
        sourceCounts[sourceName] = 0;
      }
      sourceCounts[sourceName] += count;
    });
  
    return sourceCounts;
  };
  
  const filteredRows = rows.filter(row => {
    const matchesTenant = selectedTenants.includes('All') ||
      selectedTenants.some(tenant => row.tenantName.toLowerCase() === tenant.toLowerCase());
  
    const matchesSources = selectedSources.includes('All') ||
      (Array.isArray(row.contentSources) && row.contentSources.some(source => selectedSources.includes(Object.keys(source)[0])));
  
    const matchesClients = selectedClients.includes('All') ||
      (Array.isArray(row.searchClients) && row.searchClients.some(client => selectedClients.includes(Object.keys(client)[0])));
  
    return matchesTenant && matchesSources && matchesClients;
  });
  

  const handlePageButtonClick = (newPage) => {
    setPage(newPage);
  };

  const start = page * rowsPerPage + 1;
  const end = Math.min((page + 1) * rowsPerPage, filteredRows.length);

  const totalPages = Math.ceil(filteredRows.length / rowsPerPage);

  const paginationButtons = [];
  for (let i = 1; i <= 5 && i <= totalPages; i += 1) {
    paginationButtons.push(
      <Button
        key={i}
        onClick={() => handlePageButtonClick(i - 1)}
        disabled={page === i - 1}
        className="pagination-button numbered-button" // Apply the class here
      >
        {i}
      </Button>
    );
  }

  if (totalPages > 5) {
    paginationButtons.push(
      <Typography key="dots" variant="body2" sx={{ margin: '0 8px' }}>
        ...
      </Typography>
    );

    paginationButtons.push(
      <Button
        key={totalPages}
        onClick={() => handlePageButtonClick(totalPages - 1)}
        disabled={page === totalPages - 1}
        className="pagination-button" // Apply the class here
      >
        {totalPages}
      </Button>
    );
  }

  const filteredTenants = uniqueTenantNames.filter(name =>
    name.toLowerCase().includes(tenantSearch.toLowerCase())
  );

  const filteredSources = uniqueContentSources.filter(source =>
    source.toLowerCase().includes(sourceSearch.toLowerCase())
  );

  const filteredClients = uniqueSearchClients.filter(client =>
    client.toLowerCase().includes(clientSearch.toLowerCase())
  );

  return (
    <>
      <div className="container">
        <Typography
          variant="h5"
          component="h1"
          className="heading"
          sx={{
            fontWeight: 600,
            fontSize: '24px',
            lineHeight: '29px',
            fontFamily: 'Lato, sans-serif',
            color: '#272727',
            textAlign: 'left',
            marginBottom: '16px' // Optional: Adds space below the heading
          }}
        >
          Customer List with configured Content Source and Search Client
        </Typography>
        <div className="table-container">
          <TableContainer component={Paper} sx={{ boxShadow: 'none' }}>
            {loading && <div>Loading...</div>}
            {error && !loading && <div>Error: {error}</div>}
            {!loading && !error && (
              <>
                <Table sx={{ minWidth: 650 }} aria-label="simple table">
                  <TableHead>
                    <TableRow>
                      <TableCell className='table-head' sx={{ width: '20%' }}>
                        Customer Details
                        <IconButton onClick={handleTenantClick}>
                          <SearchIcon />
                        </IconButton>  
                        <Popover
                          open={Boolean(anchorElTenant)}
                          anchorEl={anchorElTenant}
                          onClose={handleCloseTenant}
                          anchorOrigin={{
                            vertical: 'bottom',
                            horizontal: 'left',
                          }}
                          transformOrigin={{
                            vertical: 'top',
                            horizontal: 'left',
                          }}
                          PaperProps={{
                            style: {
                              width: '250px',
                            },
                          }}
                        >
                          <TextField
                            placeholder="Search..."
                            value={tenantSearch}
                            onChange={(e) => setTenantSearch(e.target.value)}
                            sx={{ m: 1, width: '94%' }}
                          />
                          <MenuItem onClick={() => handleToggleTenants('All')}>
                            <Checkbox checked={selectedTenants.includes('All')} />
                            All
                          </MenuItem>
                          {filteredTenants.map((tenant, index) => (
                            <MenuItem key={index} onClick={() => handleToggleTenants(tenant)}>
                              <Checkbox checked={selectedTenants.includes(tenant)} />
                              {tenant}
                            </MenuItem>
                          ))}
                        </Popover>                 
                        </TableCell>
                      <TableCell className='table-head' sx={{ width: '40%' }} align="left">
                        Content Sources
                        <IconButton onClick={handleSourceClick}>
                          <FilterListIcon />
                        </IconButton>
                        <Popover
                          open={Boolean(anchorElSource)}
                          anchorEl={anchorElSource}
                          onClose={handleCloseSource}
                          anchorOrigin={{
                            vertical: 'bottom',
                            horizontal: 'left',
                          }}
                          transformOrigin={{
                            vertical: 'top',
                            horizontal: 'left',
                          }}
                          PaperProps={{
                            style: {
                              width: '250px', // Adjust the width as needed
                            },
                          }}
                        >
                          <TextField
                            placeholder="Search..."
                            value={sourceSearch}
                            onChange={(e) => setSourceSearch(e.target.value)}
                            sx={{ m: 1, width: '94%'  }}
                          />
                          <MenuItem onClick={() => handleToggleSources('All')}>
                            <Checkbox checked={selectedSources.includes('All')} />
                            All
                          </MenuItem>
                          {filteredSources.map((source, index) => (
                            <MenuItem key={index} onClick={() => handleToggleSources(source)}>
                              <Checkbox checked={selectedSources.includes(source)} />
                              {source}
                            </MenuItem>
                          ))}
                        </Popover>
                      </TableCell>
                      <TableCell className='table-head' sx={{ width: '40%' }} align="left">
                        Search Clients
                        <IconButton onClick={handleClientClick}>
                          <FilterListIcon />
                        </IconButton>
                        <Popover
                          open={Boolean(anchorElClient)}
                          anchorEl={anchorElClient}
                          onClose={handleCloseClient}
                          anchorOrigin={{
                            vertical: 'bottom',
                            horizontal: 'left',
                          }}
                          transformOrigin={{
                            vertical: 'top',
                            horizontal: 'left',
                          }}
                          PaperProps={{
                            style: {
                              width: '250px', // Adjust the width as needed
                            },
                          }}
                        >
                          <TextField
                            
                            placeholder="Search..."
                            value={clientSearch}
                            onChange={(e) => setClientSearch(e.target.value)}
                            sx={{ m: 1, width: '94%'  }}
                          />
                          <MenuItem onClick={() => handleToggleClients('All')}>
                            <Checkbox checked={selectedClients.includes('All')} />
                            All
                          </MenuItem>
                          {filteredClients.map((client, index) => (
                            <MenuItem key={index} onClick={() => handleToggleClients(client)}>
                              <Checkbox checked={selectedClients.includes(client)} />
                              {client}
                            </MenuItem>
                          ))}
                        </Popover>
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                  {filteredRows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((row) => (
                    row && (
                      <TableRow key={row.tenantId}>
                        <TableCell component="th" scope="row" className='table-body' sx={{ width: '20%' }}>
                          <div>{row.tenantName}</div>
                        </TableCell>
                        <TableCell align="left">
                          <div className="card-container">
                            {Array.isArray(row.contentSources) && row.contentSources.length > 0 && Object.entries(calculateSourceCounts(row.contentSources)).map(([name, count], index) => {
                              const displayCount = count;
                              
                              return <Card key={index} text={name} count={displayCount} tooltipItems={row.contentSourceSfObjectData} />;
                            })}
                          </div>
                        </TableCell>
                        <TableCell align="left">
                          <div className="card-container">
                            {Array.isArray(row.searchClients) && row.searchClients.length > 0 && row.searchClients.map((client, index) => (
                              <Card key={index} text={Object.keys(client)[0]} count={Object.values(client)[0]} />
                            ))}
                          </div>
                        </TableCell>
                      </TableRow>
                    )
                  ))}
                </TableBody>

                </Table>
              </>
            )}
          </TableContainer>
        </div>
        <div className="pagination-container">
          <Typography variant="body2">
            Showing {start} to {end} of {filteredRows.length} entries
          </Typography>
          <div>
            <Button
              onClick={() => handlePageButtonClick(page - 1)}
              disabled={page === 0}
              className="pagination-button"
            >
              Previous
            </Button>
            {paginationButtons}
            <Button
              onClick={() => handlePageButtonClick(page + 1)}
              disabled={page >= Math.ceil(filteredRows.length / rowsPerPage) - 1}
              className="pagination-button"
            >
              Next
            </Button>
          </div>
        </div>
      </div>
    </>
  );
}